/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router';

import { MTablePagination } from '@material-table/core';
import { Visibility } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

import { CIFonts } from 'shared';

import { AlignCenter } from '../../../components/AlignCenter/AlignCenter';
import { HUMaterialTable, HUMaterialTableProps } from '../../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../../components/HUSpin/HUSpin';
import { Namespaces } from '../../../i18n';
import { useMiscellaneousStyles } from '../../../styles/miscellaneous';
import { JiraCustomerSupportTicket } from '../../../types/support/JiraSupport';
import { fetchJiraRequests } from '../../../utils/queryFns';
import { reactToCreateJiraIssue } from '../../../utils/reactions';
import { useFormatDate } from '../../../utils/useFormatDate';
import { TablePagination } from './components/TablePagination';
import { TicketCreationControl } from './components/TicketCreationControl';
import { ViewTicketModal } from './components/ViewTicketModal';
import { useJiraSupportStyles } from './styles';
import { FetchParams, JiraSupportPageProps, ModalProps, ModalTypes } from './types';

const contrastColor = ({
  bgColor = '#FFFFFF',
  fgDarkColor = '#000000',
  fgLightColor = '#FFFFFF',
  defaultColor = '#000000',
  threshold = 128,
} = {}) => {
  const hexChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F'];

  let bgColorArray = String(bgColor)
    .toUpperCase()
    .split('')
    .filter(c => hexChars.includes(c));

  switch (bgColorArray.length) {
    case 3:
    case 4:
      bgColorArray = bgColorArray.slice(0, 3).map(c => `${c}${c}`);
      break;
    case 6:
    case 8:
      bgColorArray = bgColorArray
        .slice(0, 6)
        .reduce((acc, curr, n, arr) => (n % 2 ? [...acc, `${arr[n - 1]}${curr}`] : acc), [] as string[]);
      break;
    default:
      return defaultColor;
  }

  const [r, g, b] = bgColorArray.map(h => parseInt(h, 16));
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= threshold ? fgDarkColor : fgLightColor;
};

export const JiraSupport = memo<JiraSupportPageProps>(({ user }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [fetchParams, setFetchParams] = useState<FetchParams>({
    requestStatus: 'open',
    take: 2_000_000_000,
    skip: 0,
  });
  const [modal, setModal] = useState<ModalProps | null>(null);

  const { t } = useTranslation([Namespaces.General, Namespaces.Common]);
  const { formatDate } = useFormatDate();
  const classes = useJiraSupportStyles();
  const miscClasses = useMiscellaneousStyles();

  const closeModal = () => setModal(null);

  const handleShowCreateTicketControl = () => {
    setModal({ type: ModalTypes.CREATE });
  };

  const handleOnSuccess = () => {
    void reactToCreateJiraIssue();
    setModal(null);
  };

  const {
    data: customerTickets,
    isLoading,
    error,
  } = useQuery([fetchJiraRequests.key, fetchParams], fetchJiraRequests.fn, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const jiraCustomerSupportTickets = useMemo(
    () => (customerTickets?.payload ?? []) as JiraCustomerSupportTicket[],
    [customerTickets],
  );

  const getLabelKey = useCallback((key: string) => `jira_support.labels.${key}`, []);

  const showViewTicketModal = useCallback((rowData: JiraCustomerSupportTicket | JiraCustomerSupportTicket[]) => {
    const ticket = Array.isArray(rowData) ? rowData[0] : rowData;
    setModal({ type: ModalTypes.VIEW, ticketId: ticket.issueKey });
  }, []);

  const renderStatusChip = useCallback(({ status }: JiraCustomerSupportTicket) => {
    const color = (
      {
        UNDEFINED: '#43536f',
        NEW: '#43536f',
        INDETERMINATE: '#0152cc',
        DONE: '#006644',
      } as Record<string, string>
    )[status.category];

    return (
      <Box
        style={{
          height: '34px',
          padding: '0',
          borderRadius: '16px',
          backgroundColor: color,

          border: 'none',
          cursor: 'default',
          display: 'inline-flex',
          outline: '0',
          fontSize: '0.7544642857142857rem',
          boxSizing: 'border-box',
          transition:
            'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          alignItems: 'center',
          fontFamily: CIFonts.Text,
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
          justifyContent: 'center',
          textDecoration: 'none',
        }}
      >
        <Typography
          style={{
            color: contrastColor({ bgColor: color }),
            textTransform: 'uppercase',

            paddingRight: '12px',
            paddingLeft: '12px',
            overflow: 'hidden',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '14px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.11px',
          }}
        >
          {status.name}
        </Typography>
      </Box>
    );
  }, []);

  const tableConfig = useMemo<HUMaterialTableProps<JiraCustomerSupportTicket>>(
    () => ({
      columns: [
        {
          title: t(getLabelKey('issueKey')),
          field: 'issueKey',
        },
        {
          title: t(getLabelKey('summary')),
          field: 'summary',
        },
        {
          title: t(getLabelKey('createdAt')),
          field: 'createdAt',
          render: rowData => formatDate(rowData.createdAt),
        },
        {
          title: t(getLabelKey('status')),
          field: 'status',
          render: rowData => renderStatusChip(rowData),
        },
      ],
      data: jiraCustomerSupportTickets,
      title: t('jira_support.issues'),
      actions: [
        {
          icon: () => <Visibility />,
          tooltip: t(getLabelKey('viewIssue')),
          onClick: (_, rowData) => showViewTicketModal(rowData),
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
      components: {
        Pagination: props => (
          <>
            <TablePagination {...{ fetchParams, setFetchParams, isLoading }} />
            <MTablePagination {...props} />
          </>
        ),
      },
    }),
    [
      jiraCustomerSupportTickets,
      t,
      getLabelKey,
      showViewTicketModal,
      formatDate,
      renderStatusChip,
      fetchParams,
      isLoading,
    ],
  );

  const modals = {
    [ModalTypes.CREATE]: <TicketCreationControl user={user} onClose={closeModal} onSuccess={handleOnSuccess} />,
    [ModalTypes.VIEW]: <ViewTicketModal onClose={closeModal} ticketId={modal?.ticketId} />,
  };

  useEffect(() => {
    if (queryParams.get('cluster')) {
      handleShowCreateTicketControl();
    }
    // Adding `queryParams` as a dependency causes the page to freeze
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.container}>
          <Box className={classes.title}>{t('jira_support.title')}</Box>
          <Button variant="contained" className={classes.newIssueButton} onClick={handleShowCreateTicketControl}>
            {t('jira_support.new_issue')}
          </Button>
        </Box>
        {isLoading ? (
          <AlignCenter className={miscClasses.loaderContainer}>
            <CircularProgress color="primary" size={40} />
          </AlignCenter>
        ) : error ? (
          <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
        ) : (
          <HUSpin spinning={isLoading} delay={50}>
            <HUMaterialTable {...tableConfig} />
          </HUSpin>
        )}
      </Box>
      {modal && modals[modal.type]}
    </>
  );
});
